/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput.tsx';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import { useInSectionEditForm } from 'components/section-edit/SectionEdit';
import { format } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { formatTimeOfDay } from 'utils/date';
import { t } from 'utils/getTranslations';
import { isNewDesignCopy } from 'utils/newDesignCopy';
import { TimecardFormState } from './useTimecardForm';

/** The author, or the authors of a multi-author timecard, and the date. */
export const AuthorFields = ({ form }: { form: TimecardFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    // The section's grid cell is the field's width; the full form's column halves it.
    const isInSection = useInSectionEditForm();
    const dateFormat = useEffectiveDateFormat();
    const {
        isMultiSelectMode,
        addCurrentUserAsAuthorLink,
        showPreviousTimecardEntry,
        lastTimecardCreatedAt,
        type,
        getTimecardMinDate,
        validateTimecardDate,
    } = form;

    return (
        <>
            {isMultiSelectMode ? (
                <DynamicMultiSelectInput
                    control={control}
                    name="employees"
                    label="timecards.form.employee"
                    placeholder="timecards.form.select-employee"
                    setValue={setValue}
                    getOptionLabel="name"
                    getOptionIdentifier="id"
                    required
                    paginated={{
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                    }}
                    bottomRightLabelComponent={addCurrentUserAsAuthorLink}
                />
            ) : (
                <DynamicSelectInput
                    setValue={setValue}
                    control={control}
                    label="timecards.form.employee"
                    required
                    name="employee"
                    paginated={{
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                    }}
                    getOptionLabel="name"
                    placeholder="timecards.form.select-employee"
                    getOptionIdentifier="id"
                    bottomRightLabelComponent={
                        // Mutually exclusive with the shortcut: the previous-timecard
                        // hint only resolves once an author is selected.
                        addCurrentUserAsAuthorLink ??
                        (showPreviousTimecardEntry && lastTimecardCreatedAt && type === 'add' ? (
                            <span className="text-caption text-fg-secondary">
                                {/* The new design: the company's date and the locale's time, in one message. */}
                                {isNewDesignCopy() ? (
                                    t('timecards.form.previous-timecard-at', {
                                        date: format(new Date(lastTimecardCreatedAt), dateFormat),
                                        time: formatTimeOfDay(lastTimecardCreatedAt),
                                    })
                                ) : (
                                    <>
                                        {t('timecards.form.previous-timecard')}:{' '}
                                        {format(new Date(lastTimecardCreatedAt), "MMM dd, yyyy 'at' HH:mm")}
                                    </>
                                )}
                            </span>
                        ) : undefined)
                    }
                />
            )}
            <DateInput
                maxDate={new Date()}
                minDate={getTimecardMinDate()}
                control={control}
                name="timecardDate"
                required
                label="timecards.form.date"
                validate={validateTimecardDate}
                useIncrementDecrementButtons
                setValue={setValue}
                fullWidth={isInSection}
            />
        </>
    );
};
